import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import StructuredData, { getOrganizationSchema, getWebSiteSchema, getWebPageSchema } from '@/components/feature/StructuredData';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { getLatestArticles, getPopularArticles } from '@/mocks/articles';

export default function Home() {
  useEffect(() => {
    document.title = '打屁股视频大全 - 精选合集推荐 | 亲子教育·影视赏析·搞笑短片';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', '打屁股视频大全汇聚亲子教育情景、影视片段赏析、搞笑整蛊短片等多种类型视频合集，帮助用户快速找到所需打屁股视频资源，了解不同场景下的视频分类与推荐。');
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', '打屁股视频,打屁股视频合集,打屁股视频推荐,搞笑打屁股视频,亲子教育视频,影视打屁股片段,打屁股短视频');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', 'https://dapiguvideo.com/');
  }, []);

  const latestArticles = useMemo(() => getLatestArticles(6), []);
  const popularArticles = useMemo(() => getPopularArticles(6), []);

  return (
    <>
      <ScrollToTop />
      <StructuredData data={getOrganizationSchema()} />
      <StructuredData data={getWebSiteSchema()} />
      <StructuredData
        data={getWebPageSchema(
          '打屁股视频大全 - 精选合集推荐',
          '汇聚打屁股视频相关内容合集，涵盖亲子教育情景、影视片段赏析、搞笑整蛊短片等多种类型',
          'https://dapiguvideo.com/',
        )}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20cozy%20home%20entertainment%20scene%20with%20large%20TV%20screen%20displaying%20colorful%20video%20thumbnails%2C%20comfortable%20sofa%20with%20pillows%2C%20soft%20ambient%20lighting%2C%20modern%20living%20room%20interior%2C%20evening%20atmosphere%2C%20cinematic%20mood%2C%20warm%20amber%20and%20cream%20tones%2C%20inviting%20and%20relaxing%20vibe&width=1600&height=900&seq=hero-home-2026&orientation=landscape"
            alt="打屁股视频大全 - 精选视频合集推荐平台"
            title="打屁股视频大全 - 精选视频合集推荐"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/15 to-black/40"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-4 max-w-3xl leading-tight">
              打屁股视频大全
            </h1>
            <p className="text-sm md:text-lg text-background-50/85 max-w-2xl mb-8 leading-relaxed">
              精选亲子教育情景、影视片段赏析、搞笑整蛊短片等优质视频合集，为您带来丰富多彩的视听体验
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <Link
                to="/category/qinzi-jiaoyu"
                className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
              >
                立即浏览
              </Link>
              <Link
                to="/about"
                className="px-6 py-3 rounded-lg bg-background-50/15 text-background-50 font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-background-50/25 backdrop-blur-sm transition-colors"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              内容分类导航
            </h2>
            <p className="text-sm text-foreground-500 max-w-xl mx-auto">
              六大精选分类，涵盖亲子教育、影视赏析、搞笑短片、历史文化、动漫游戏与社会热点
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group flex flex-col items-center p-5 rounded-lg bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 cursor-pointer transition-all duration-300"
              >
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors mb-3">
                  <i className={`${cat.icon} text-xl`}></i>
                </span>
                <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-700 text-center whitespace-nowrap">
                  {cat.name}
                </span>
                <span className="text-xs text-foreground-400 mt-1">
                  {cat.articleCount} 篇内容
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* Latest Articles */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                  最新更新
                </h2>
                <p className="text-sm text-foreground-500">持续更新，发现更多精彩内容</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {latestArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* Popular Articles */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                热门推荐
              </h2>
              <p className="text-sm text-foreground-500">最受用户欢迎的精选内容</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {popularArticles.map((article) => (
              <ArticleCard key={article.slug} article={article} />
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative w-full py-14 md:py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20warm%20gradient%20background%20with%20soft%20flowing%20curves%20and%20organic%20shapes%2C%20amber%20and%20cream%20color%20palette%2C%20minimal%20modern%20design%2C%20smooth%20textures%2C%20elegant%20atmosphere%2C%20no%20text%2C%20clean%20composition&width=1600&height=700&seq=cta-home-2026&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/15 to-black/30"></div>
          <div className="relative z-10 text-center px-4 max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
              发现更多精彩视频内容
            </h2>
            <p className="text-sm md:text-base text-background-50/80 mb-6 leading-relaxed">
              六大分类、数千精选内容，持续更新中。无论您对什么类型的视频感兴趣，都能在这里找到心头好。
            </p>
            <Link
              to="/category/gaoxiao-duanpian"
              className="inline-flex px-6 py-3 rounded-lg bg-accent-500 text-background-50 font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-accent-600 transition-colors"
            >
              立即探索
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}